import StepperModel from '../../../models/StepperModel';
import { STEP_PREPARING_WEBSITE } from '../../../steps/steps-definitions';
import { useCallback } from 'react';
import { toJS } from 'mobx';
import Api from '../../../services/Api';
import SiteMapModel from '../../../steps/Sitemap/models/SiteMapModel';
import BusinessModel from '../../../steps/FindYourBusiness/models/BusinessModel';
import WizardSessionData from '../../../services/WizardSessionData';
import SiteBuilderModel from '../../../steps/SiteBuilder/model/SiteBuilderModel';

const useSavePageStructure = () => {
  const save = useCallback(async () => {
    const fullSitemap = toJS(SiteMapModel.treeSiteMap);

    const newOrModifiedPages = fullSitemap.filter((node) => {
      if (
        node.id === SiteMapModel.SITEMAP_ROOT_INTERNAL ||
        node.id === SiteMapModel.SITEMAP_ROOT_EXTERNAL
      ) {
        return false;
      }
      if (node.data?.shouldRegenerateContent === true) return true;
      if (node.data?.isTextGenerated === true) return false;
      if (node.data?.movedFromExternal === true) return true;
      if (node.data?.isCustomPage || node.data?.origin === 'custom')
        return true;
      return false;
    });

    const sitemapTree = SiteMapModel.getSiteMapFinalTree();
    const category = BusinessModel.selectedCategoryName;
    const sessionData = WizardSessionData.getWizardData();
    const allAreasTexts = { data: {} };
    const newPageIds = newOrModifiedPages.map((p) => p.id);

    if (newPageIds.length === 0) {
  syncGeneratedPagesWithSitemap();
  // Persist the current sitemap nodes into the sitemap wizard data so
  // WizardSessionData includes changes made in Manage Sitemap when the
  // session is saved (e.g. on publish).
  const sitemapNodesForSession = SiteMapModel.getSiteMapNodesForSessionStorage();
  SiteMapModel.siteMapWizardData.setFormData({ siteMap: sitemapNodesForSession.siteMap });

  await SiteBuilderModel.updatePreviewData();
      return;
    }

    const results: Array<{
      pageId: string | number;
      pageName: string;
      success: boolean;
      // texts/images reflect the regenerate API payload
      texts?: Array<{ name: string; languageCode?: string; text: string }>;
      images?: Array<{
        name: string;
        image?: { ObjectURL?: string; PresignedUrl?: string };
      }>;
      error?: any;
    }> = [];

    await Promise.all(
      newPageIds.map(async (pageId, i) => {
        const pageName = newOrModifiedPages[i].text;
        try {
          // clear page elements
          const UserPreviewDataModel = require('../../../steps/SiteBuilder/model/user-preview-data/UserPreviewDataModel').default;
          if (UserPreviewDataModel.pageElements && UserPreviewDataModel.pageElements[pageId]) {
            delete UserPreviewDataModel.pageElements[pageId];
          }

          const response = await Api.regeneratePageResources(
            allAreasTexts,
            category,
            sitemapTree,
            pageId,
            sessionData
          );
          // API returns { status: 'success', data: { texts: [...], images: [...] } }
          const payload = response.data;
          const texts = payload.texts || [];
          const images = payload.images || [];
          //clears image if in the images objects, there is no value "image"
          const nonEmptyImages = (imagesArray: Array<any>) => {
            return imagesArray.filter((img) => img.image);
          };
          results.push({
            pageId,
            pageName,
            success: true,
            texts,
            images: nonEmptyImages(images),
          });
          const pageNode = SiteMapModel.treeSiteMap.find(
            (n) => n.id === pageId
          );
          if (pageNode) {
            pageNode.data.isTextGenerated = true;
            // Зануляваме флага за регенериране след успешна регенерация
            pageNode.data.shouldRegenerateContent = false;
          }
        } catch (error) {
          results.push({ pageId, pageName, success: false, error });
        }
      })
    );

    // Build a map of the newly generated content keyed by pageId
    const regeneratedByPage = new Map<
      string | number,
      {
        texts: Array<{ name: string; languageCode?: string; text: string }>;
        images: Array<{
          name: string;
          image?: { ObjectURL?: string; PresignedUrl?: string };
        }>;
      }
    >();

    results
      .filter((r) => r.success)
      .forEach((r) => {
        regeneratedByPage.set(r.pageId, {
          texts: r.texts || [],
          images: r.images || []
        });
      });

    // Sync generatedPages with current sitemap, preserving existing content
    // and merging in newly generated texts/images for affected pages
  syncGeneratedPagesWithSitemap(regeneratedByPage);
  // Persist the updated sitemap into the sitemap wizard data so the
  // session payload (WizardSessionData) will contain the pages added/edited
  // via Manage Sitemap.
  const sitemapNodesForSession = SiteMapModel.getSiteMapNodesForSessionStorage();
  SiteMapModel.siteMapWizardData.setFormData({ siteMap: sitemapNodesForSession.siteMap });

  await SiteBuilderModel.updatePreviewData();
  }, []);

  function syncGeneratedPagesWithSitemap(
    regeneratedByPage?: Map<
      string | number,
      {
        texts: Array<{ name: string; languageCode?: string; text: string }>;
        images: Array<{
          name: string;
          image?: { ObjectURL?: string; PresignedUrl?: string };
        }>;
      }
    >
  ) {
    const PageGeneratedData =
      require('../../../steps/SiteBuilder/model/generated-data/PageGeneratedData').default;
    const existingById = new Map(
      (SiteBuilderModel.generatedPages || []).map((pg) => [pg.getId(), pg])
    );
    // Filter out root nodes
    const nodes = SiteMapModel.treeSiteMap
      .filter(
        (node) =>
          node.id !== SiteMapModel.SITEMAP_ROOT_INTERNAL &&
          node.id !== SiteMapModel.SITEMAP_ROOT_EXTERNAL
      );
    const pages = nodes.map((node) => {
      const existing = existingById.get(node.id);
      const regenerated = regeneratedByPage?.get(node.id);

      // Preserve existing generated page content by default
      const existingTextAreas = existing?.getTextAreas?.() || [];
      const existingPictureAreas = existing?.getPictureAreas?.() || [];

      // If we have newly generated texts/images for this page, merge/replace
      const mergedTextAreas = regenerated
        ? regenerated.texts.map((t) => ({
            name: t.name,
            text: t.text,
            // keep language from API if provided, fallback to 'de'
            languageCode: t.languageCode || 'de'
          }))
        : existingTextAreas;

      const mergedPictureAreas = regenerated
        ? regenerated.images.map((img) => {
            const objectUrl = img.image?.ObjectURL ?? '';
            const presigned = img.image?.PresignedUrl ?? '';
            return {
              name: img.name,
              image: {
                // Ensure PresignedUrl is present as the source of truth
                ObjectURL: objectUrl,
                PresignedUrl: presigned
              }
            };
          })
        : existingPictureAreas;

      return new PageGeneratedData({
        id: node.id,
        parent: node.parent,
        type: node.data?.type,
        pageName: node.data?.pageName || node.text,
        pagePath: node.data?.path,
        isCustomPage: node.data?.isCustomPage,
        isPageExternal: node.data?.isPageExternal,
        textAreas: mergedTextAreas,
        pictureAreas: mergedPictureAreas
      });
    });
    SiteBuilderModel.setGeneratedPages(pages);

    // Force refresh of the current page in preview
    const currentPage = SiteBuilderModel.currentShownInPreviewGeneratedPage;
    let pageToShow = null;
    if (currentPage) {
      pageToShow = pages.find((pg) => pg.getId() === currentPage.getId());
    }
    // If current page is missing, fallback to Startseite
    if (!pageToShow) {
      pageToShow = pages.find((pg) => pg.getType && pg.getType() === 'Startseite');
    }
    if (pageToShow) {
      SiteBuilderModel.setCurrentShownInPreviewGeneratedPage(pageToShow);
    }
  }

  return save;
};

export default useSavePageStructure;
